import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { firestore as db } from "../FirebaseConfig";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Reviews from "./Review";
import QuantitySelector from "./QuantitySelector";
import Description from "./Description";
import AdditionalInfo from "./AdditionalInfo";
import MoreProd from "../Product_Page/Components/MoreProd";
import { auth } from "../FirebaseConfig";
import Lottie from "lottie-react";
import BestSellingProduct from "./PopularProducts";
// import loadingAnimation from "../../public/assets/General/Animation - 1741328716471.json"; // Ensure the JSON file is placed correctly




const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [stockWarning, setStockWarning] = useState("");
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(id);
  const [addingToCart, setAddingToCart] = useState(false);
  const [animationData, setAnimationData] = useState(null);


  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchProduct = async () => {
      if (!id) return;
      try {
        const productRef = doc(db, "products", id);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          const productData = productSnap.data();
          setProduct(productData);

          if (productData.variants) {
            await fetchVariants(productData.variants);
          }
        } else {
          console.error("Product not found");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      }
      setLoading(false);
      window.scrollTo(0, 0);
    };

    const fetchVariants = async (variantArray) => {
      let variantDetails = [];
      for (const variant of variantArray) {
        const variantRef = doc(db, "products", variant.id);
        const variantSnap = await getDoc(variantRef);
        if (variantSnap.exists()) {
          const variantData = variantSnap.data();
          const discountPrice =
            variantData.prices.productPrice - variantData.prices.productDiscount;
          const discountPercentage = (
            (variantData.prices.productDiscount /
              variantData.prices.productPrice) *
            100
          ).toFixed(0);

          variantDetails.push({
            id: variant.id,
            stock: variantData.stock,
            price: variantData.prices.productPrice,
            discount: variantData.prices.productDiscount,
            discountPrice,
            discountPercentage,
            weight: variantData.weight,
          });
        }
      }
      setVariants(variantDetails);
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product && quantity > product.stock) {
      setStockWarning(`Only ${product.stock} items available in stock.`);
    } else {
      setStockWarning("");
    }
  }, [quantity, product]);

  const handleVariantChange = (variantId) => {
    navigate(`/product/${variantId}`); // Redirect to variant's product page
    setSelectedVariant(variantId);
  };


  const handleBuyNow = () => {
    if (quantity > product.stock) {
      alert("Selected quantity exceeds stock availability.");
      return;
    }
  
    navigate(`/checkout?productId=${id}&quantity=${quantity}`);
  };
  

  const handleAddToCart = async () => {
    if (!auth.currentUser) {
      alert("Please log in to add items to your cart.");
      navigate(`/login?redirect=/product/${id}`);
      return;
    }

    if (quantity > product.stock) return;

    setAddingToCart(true);
    const userId = auth.currentUser.uid;
    const cartRef = doc(db, "customers", userId);

    try {
      const cartSnap = await getDoc(cartRef);

      if (cartSnap.exists()) {
        const cartData = cartSnap.data();
        let updatedCart = { ...cartData };

        if (updatedCart.items) {
          const existingItemIndex = updatedCart.items.findIndex(item => item.productId === id);

          if (existingItemIndex !== -1) {
            updatedCart.items[existingItemIndex].quantity += quantity;
          } else {
            updatedCart.items.push({ productId: id, quantity });
          }
        } else {
          updatedCart = { items: [{ productId: id, quantity }] };
        }

        await updateDoc(cartRef, updatedCart);
      } else {
        await setDoc(cartRef, { items: [{ productId: id, quantity }] });
      }

      alert("Item added to cart!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Error adding item to cart. Please try again.");
    }

    setAddingToCart(false);
  };

  useEffect(() => {
    // Fetch Lottie animation from the public folder
    fetch("/assets/General/loadingBee.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => console.error("Error loading animation:", error));
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        {animationData ? (
          <Lottie animationData={animationData} loop autoPlay style={{ width: "50%", maxWidth: "300px" }} />
        ) : (
          <p>Loading...</p>
        )}
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="h-screen flex justify-center items-center text-xl">
        Product not found.
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 mt-20">
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-8 items-center">
        {/* Swiper Carousel */}
        <div className="relative">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={10}
            slidesPerView={1}
            // navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            className="rounded-lg shadow-lg"
          >
            {product.images.map((image, index) => (
              <SwiperSlide key={index}>
                <div className="flex justify-center">
                  <img
                    src={image}
                    alt={product.name}
                    className="rounded-lg w-1/1 object-contain transition-transform hover:scale-105 duration-300"
                    loading="lazy"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Product Details */}
        <div className="animate-fade-in">
          <h1 className="text-4xl font-bold text-orange-500 mb-2 mt-2">
            {product.name}
          </h1>
          <h3 className="text-gray-900 mb-2 text-lg sm:text-2xl">
            Category: {product.category}
          </h3>
          <p className="text-gray-700 mb-6 text-sm leading-relaxed">
            {product.description}
          </p>

          {/* Variants Section */}
          {variants.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Select a Variant:</h3>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                {variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleVariantChange(variant.id)}
                    className={`border p-4 rounded-md text-sm font-medium transition flex flex-col items-center ${
                      variant.stock === 0
                        ? "border-gray-300 text-gray-500 cursor-not-allowed bg-gray-100"
                        : selectedVariant === variant.id
                        ? "border-green-500 bg-green-100 text-green-700"
                        : "border-gray-300 text-gray-700 hover:border-orange-500"
                    }`}
                    disabled={variant.stock === 0}
                  >
                    {variant.weight*1000} g <br />
                    <span className="text-xs text-gray-500">
                      ₹{variant.discountPrice} ({variant.discountPercentage}% OFF)
                    </span>
                    {variant.stock <= 10 && variant.stock > 0 && (
                      <span className="text-red-500 text-xs">
                        Only {variant.stock} left in stock!
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
 <div className="mt-4">
            <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
            {stockWarning && <p className="text-red-500 text-sm mt-1">{stockWarning}</p>}
          </div>


{/* Mobile Sticky Bottom Bar - Only Visible on Mobile */}
<div className="fixed bottom-0 z-50 left-0 right-0 sm:hidden bg-gradient-to-t from-white to-gray-100 border-t p-3 flex justify-center items-center shadow-xl">
  <div className="w-full max-w-lg flex gap-3">
    <button
      onClick={handleAddToCart}
      className={`flex-1 py-3 rounded-full text-center font-semibold transition-all duration-300 shadow-md ${
        addingToCart || quantity > product.stock
          ? "bg-gray-400 text-gray-700 cursor-not-allowed"
          : "bg-orange-500 text-white hover:bg-orange-600 active:scale-95"
      }`}
      disabled={addingToCart || quantity > product.stock}
    >
      {addingToCart ? "Adding..." : "Add to Cart"}
    </button>

    <button
      onClick={handleBuyNow}
      className="flex-1 py-3 rounded-full text-center font-semibold bg-green-500 text-white hover:bg-green-600 shadow-md active:scale-95 transition-all duration-300"
      disabled={quantity > product.stock}
    >
      ⚡ Buy Now
    </button>
  </div>
</div>


          {/* Buttons */}
          <div className="flex gap-4 mt-6">
          <button
              onClick={handleAddToCart}
              className={`py-3 px-6 rounded-md transition duration-300 ${
                addingToCart || quantity > product.stock
                  ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                  : "bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg"
              }`}
              disabled={addingToCart || quantity > product.stock}
            >
              {addingToCart ? "Adding..." : "Add to Cart"}
            </button>

            <button
              onClick={handleBuyNow}
              className="bg-green-500 text-white py-3 px-6 rounded-md hover:bg-green-600 hover:shadow-lg transition duration-300"
              disabled={quantity > product.stock}
            >
              Buy Now
            </button>

          </div>
        </div>
      </div>
      <Description Description={product.longDescription} />

      {product && <AdditionalInfo weight={product.weight ? parseFloat(product.weight) : 0} />}
      <BestSellingProduct/>
      {/* <MoreProd data={[]} /> */}
      <Reviews id={id} />
    </div>
  );
};

export default ProductPage;
